/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useCallback, useMemo} from 'react'
import PropTypes from 'prop-types'

import {useIntl} from 'react-intl'
import {parsePriceValue} from '../utils/parse-price-value'
import {useCurrency} from '../../../hooks'
import SelectedFilters from '../../../components/filter-sort/selected-filters'

const SelectedRefinements = ({
    hasSelectedRefinements,
    resetFilters,
    resetLabel,
    selectedFilterValues,
    toggleFilter
}) => {
    const {formatNumber} = useIntl()
    const {currency} = useCurrency()

    const formatPriceLabel = useCallback(
        (filter) => {
            const priceValues = parsePriceValue(filter)
            const minLabel = formatNumber(priceValues[0], {
                style: 'currency',
                currency: currency
            })

            const maxLabel = formatNumber(priceValues[1], {
                style: 'currency',
                currency: currency
            })
            return `${minLabel} - ${maxLabel}`
        },
        [currency, formatNumber]
    )

    const selectedFilters = useMemo(() => {
        const selectedFilters = []
        for (const key in selectedFilterValues) {
            const filters = selectedFilterValues[key].label.split('|')
            filters?.forEach((filter, index) => {
                const selected = {
                    label: key === 'price' ? formatPriceLabel(filter) : filter,
                    value: key,
                    apiLabel: selectedFilterValues[key].value.split('|')[index]
                }

                selectedFilters.push(selected)
            })
        }
        return selectedFilters
    }, [formatPriceLabel, selectedFilterValues])

    const onRemove = useCallback(
        (filter) => {
            toggleFilter({value: filter.apiLabel}, filter.value, true)
        },
        [toggleFilter]
    )

    if (!selectedFilters?.length && !hasSelectedRefinements) {
        return null
    }

    return (
        <SelectedFilters
            filters={selectedFilters}
            resetLabel={resetLabel}
            onRemove={onRemove}
            onReset={resetFilters}
        />
    )
}

SelectedRefinements.propTypes = {
    hasSelectedRefinements: PropTypes.bool,
    selectedFilterValues: PropTypes.object,
    toggleFilter: PropTypes.func,
    resetFilters: PropTypes.func,
    resetLabel: PropTypes.string
}

export default SelectedRefinements
